import { parseDelimited, toDelimited } from "@/lib/tool-kit/text/delimited";

/**
 * Names, places and organisations for the anonymizer: the reader's own term lists, the
 * hints a transcript gives about who is speaking, a list of capitalised words worth a
 * second look, and the key file that records the pseudonym each real value received.
 */

export type ListCategory = "person" | "place" | "organisation" | "other";

export const LIST_CATEGORIES: readonly ListCategory[] = [
  "person",
  "place",
  "organisation",
  "other",
];

export type AnonCategory =
  | ListCategory
  | "email"
  | "phone"
  | "url"
  | "handle"
  | "ip"
  | "postcode"
  | "id"
  | "date";

export const ANON_CATEGORIES: readonly AnonCategory[] = [
  "person",
  "place",
  "organisation",
  "other",
  "email",
  "phone",
  "url",
  "handle",
  "ip",
  "postcode",
  "id",
  "date",
];

export interface Entity {
  category: ListCategory;
  /** Every spelling that stands for the same thing. */
  variants: string[];
  /** A pseudonym the reader chose (`Sarah => Alice`), or one carried in from a key. */
  replacement?: string;
  /** Found by a hint in the transcript rather than listed by the reader. */
  implicit?: boolean;
}

export interface KeyRow {
  category: AnonCategory;
  original: string;
  pseudonym: string;
  count: number;
}

const RE_COMMENT = /^\s*#/;
const MIN_VARIANT = 2;
const REPLACEMENT_MARK = "=>";
const VARIANT_MARK = "|";

/** One term per line; `|` separates spellings; `=> Alice` fixes the pseudonym. */
export function parseTermList(
  source: string,
  category: ListCategory
): Entity[] {
  const entities: Entity[] = [];
  for (const line of source.split(RE_LINES)) {
    if (RE_COMMENT.test(line) || line.trim() === "") {
      continue;
    }
    const arrow = line.lastIndexOf(REPLACEMENT_MARK);
    const left = arrow >= 0 ? line.slice(0, arrow) : line;
    const right = arrow >= 0 ? line.slice(arrow + REPLACEMENT_MARK.length) : "";
    const variants = left
      .split(VARIANT_MARK)
      .map((part) => part.trim())
      .filter((part) => part.length >= MIN_VARIANT);
    if (variants.length === 0) {
      continue;
    }
    const replacement = right.trim();
    entities.push({
      category,
      variants,
      ...(replacement ? { replacement } : {}),
    });
  }
  return entities;
}

const RE_TITLE_PREFIX =
  /^(?:Dr|Mr|Mrs|Ms|Miss|Mx|Prof|Professor|Sir|Dame|Rev|Fr|Sgt|Capt|Judge)\.?\s+/;

export function stripTitle(name: string): string {
  return name.replace(RE_TITLE_PREFIX, "").trim();
}

const NAME_WORD = "\\p{Lu}[\\p{L}'’-]+";
const RE_SPEAKER_LABEL =
  /^[ \t]*(?:\[[^\]\n]*\][ \t]*)?(\p{Lu}[\p{L}'’.-]*(?:[ \t]+\p{Lu}[\p{L}'’.-]*){0,2})[ \t]*:/gmu;
const RE_NAME_FIELD = new RegExp(
  `^[ \\t]*(?:full name|participant name|interviewee name|respondent name|interviewee|name)[ \\t]*:[ \\t]*(${NAME_WORD}(?:[ \\t]+${NAME_WORD}){0,2})[ \\t]*$`,
  "gmiu"
);
const RE_TITLED = new RegExp(
  `\\b(?:Dr|Mr|Mrs|Ms|Miss|Mx|Prof|Professor|Sir|Dame|Rev|Fr|Sgt|Capt|Judge)\\b\\.?[ \\t]+(${NAME_WORD}(?:[ \\t]+${NAME_WORD})?)`,
  "gu"
);
const RE_INTRO = new RegExp(
  `(?:[Mm]y name(?:'s|’s| is)|[Cc]all me)[ \\t]+(${NAME_WORD}(?:[ \\t]+${NAME_WORD})?)`,
  "gu"
);
const RE_GENERIC_SPEAKER =
  /^(?:interviewer|interviewee|participant|respondent|moderator|facilitator|researcher|speaker|host|guest|narrator|unknown|unidentified|me|q|a|int|res|note|notes|transcript|time|date|location|summary|question|answer|title|topic|theme|code|re|fwd|subject|name|age|gender|duration|setting|context|interview|focus group|group)$/i;
const RE_CODE_LIKE = /^[A-Za-z]{1,3}[ -]?\d+$/;
const MIN_NAME = 2;
const RE_LINES = /\r?\n/;
const RE_END_DOT = /\.$/;
const RE_SPACE_RUN = /[ \t]+/g;

function isPlausibleName(value: string): boolean {
  const name = stripTitle(value.replace(RE_END_DOT, ""));
  return (
    name.length >= MIN_NAME &&
    !RE_GENERIC_SPEAKER.test(name) &&
    !RE_CODE_LIKE.test(name)
  );
}

/** Names the transcript itself points at: speaker labels, titled names, introductions. */
export function hintedNames(
  text: string,
  use: { speakers: boolean; titles: boolean; intros: boolean }
): string[] {
  const found: string[] = [];
  const collect = (regex: RegExp, strip: boolean) => {
    for (const match of text.matchAll(regex)) {
      const raw = match[1] ?? "";
      if (isPlausibleName(raw)) {
        found.push(strip ? stripTitle(raw.replace(RE_END_DOT, "")) : raw);
      }
    }
  };
  if (use.speakers) {
    collect(RE_SPEAKER_LABEL, true);
    collect(RE_NAME_FIELD, true);
  }
  if (use.titles) {
    collect(RE_TITLED, false);
  }
  if (use.intros) {
    collect(RE_INTRO, false);
  }
  return found;
}

export interface NameCandidate {
  text: string;
  count: number;
}

const RE_CAPITALISED_RUN = /\p{Lu}[\p{Ll}'’-]+(?:[ \t]+\p{Lu}[\p{Ll}'’-]+)*/gu;
const RE_SENTENCE_END = /[.!?…:;\n\r"“”‘([]/;
const NOT_NAMES = new Set(
  "i i'm i'll i've i'd monday tuesday wednesday thursday friday saturday sunday january february march april may june july august september october november december mum mom dad god internet english yes okay ok well so but and the".split(
    " "
  )
);
const MAX_CANDIDATES = 30;
const MIN_CANDIDATE = 3;

function atSentenceStart(text: string, index: number): boolean {
  for (let i = index - 1; i >= 0; i--) {
    const char = text[i] ?? "";
    if (char === " " || char === "\t") {
      continue;
    }
    return RE_SENTENCE_END.test(char);
  }
  return true;
}

interface Run {
  phrase: string;
  start: number;
  sentenceStart: boolean;
}

function capitalisedRuns(text: string): Run[] {
  const runs: Run[] = [];
  for (const match of text.matchAll(RE_CAPITALISED_RUN)) {
    if (match.index !== undefined) {
      runs.push({
        phrase: match[0].replace(RE_SPACE_RUN, " "),
        start: match.index,
        sentenceStart: atSentenceStart(text, match.index),
      });
    }
  }
  return runs;
}

function isNameLike(phrase: string): boolean {
  return (
    phrase.length >= MIN_CANDIDATE &&
    !phrase.split(" ").every((word) => NOT_NAMES.has(word.toLowerCase()))
  );
}

/**
 * Capitalised words that no replacement covered — the names, places and employers a
 * human should look at. A sentence's first word is capitalised whatever it is, so a
 * phrase is only suggested when it also appears in the middle of a sentence; a sentence
 * that opens with a phrase already seen elsewhere (or with a word and then that phrase)
 * adds to its count.
 */
export function suggestCandidates(
  text: string,
  covered: readonly { start: number; end: number }[]
): NameCandidate[] {
  const free = capitalisedRuns(text).filter(
    (run) =>
      !covered.some(
        (range) =>
          run.start < range.end && run.start + run.phrase.length > range.start
      )
  );
  const counts = new Map<string, number>();
  for (const run of free) {
    if (!run.sentenceStart && isNameLike(run.phrase)) {
      counts.set(run.phrase, (counts.get(run.phrase) ?? 0) + 1);
    }
  }
  for (const run of free) {
    if (!run.sentenceStart) {
      continue;
    }
    const rest = run.phrase.slice(run.phrase.indexOf(" ") + 1);
    const hit = [run.phrase, rest].find((phrase) => counts.has(phrase));
    if (hit) {
      counts.set(hit, (counts.get(hit) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([phrase, count]) => ({ text: phrase, count }))
    .sort((a, b) => b.count - a.count || a.text.localeCompare(b.text))
    .slice(0, MAX_CANDIDATES);
}

/** Read a key file (CSV with category, original, pseudonym) back in. */
export function parseKey(csv: string): KeyRow[] {
  if (csv.trim() === "") {
    return [];
  }
  const rows: KeyRow[] = [];
  const known = new Set<string>(ANON_CATEGORIES);
  for (const cells of parseDelimited(csv, ",").rows) {
    const category = (cells[0] ?? "").trim().toLowerCase();
    const original = cells[1] ?? "";
    const pseudonym = cells[2] ?? "";
    if (known.has(category) && original !== "" && pseudonym !== "") {
      rows.push({
        category: category as AnonCategory,
        original,
        pseudonym,
        count: 0,
      });
    }
  }
  return rows;
}

export function keyToCsv(
  rows: readonly KeyRow[],
  head: readonly [string, string, string, string]
): string {
  return toDelimited(
    [
      [...head],
      ...rows.map((row) => [
        row.category,
        row.original,
        row.pseudonym,
        String(row.count),
      ]),
    ],
    ","
  );
}
